import axios from 'axios';
import { clearClientsSnapshot } from './clientsSnapshotStorage';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

// Create axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      clearClientsSnapshot();
      // Redirect to login, preserving the base path
      const currentPath = window.location.pathname;
      let loginPath = '/login';
      if (currentPath.startsWith('/admin')) {
        loginPath = '/admin/login';
      } else if (currentPath.startsWith('/tech')) {
        loginPath = '/tech/login';
      }
      window.location.href = loginPath;
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => apiClient.post('/auth/login', credentials),
  register: (userData) => apiClient.post('/auth/register', userData),
  getProfile: () => apiClient.get('/auth/profile'),
  updateProfile: (data) => apiClient.put('/auth/profile', data),
  changePassword: (data) => apiClient.put('/auth/change-password', data),
  forgotPassword: (email) => apiClient.post('/auth/forgot-password', { email }),
  resetPassword: (data) => apiClient.post('/auth/reset-password', data),
  verifyResetToken: (token) => apiClient.get(`/auth/verify-reset-token/${token}`),
};

const sanitizeParams = (params = {}) => {
  const sanitized = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }
    if (typeof value === 'string' && value.trim() === '') {
      return;
    }
    sanitized[key] = value;
  });
  return sanitized;
};

// Users API
export const usersAPI = {
  getUsers: (params) => apiClient.get('/users', { params: sanitizeParams(params) }),
  getUser: (id) => apiClient.get(`/users/${id}`),
  updateUser: (id, data) => apiClient.put(`/users/${id}`, data),
  deactivateUser: (id) => apiClient.delete(`/users/${id}`),
  reactivateUser: (id) => apiClient.patch(`/users/${id}/reactivate`),
  deleteUser: (id) => apiClient.delete(`/users/${id}/delete`),
  undeleteUser: (id) => apiClient.patch(`/users/${id}/undelete`),
  getDeletedUsers: (params) => apiClient.get('/users/deleted', { params: sanitizeParams(params) }),
  resetPassword: (id, newPassword) => apiClient.post(`/users/${id}/reset-password`, { newPassword }),
};

// Clients API
// Geocoding auto-repairs bad zip/state data (nearby zip + adjacent-state
// lookups) before falling back through Census/Nominatim, so a pathological
// address can occasionally take a bit longer than the default 10s timeout.
// Bulk endpoints loop this over every affected customer server-side, so
// they need much more headroom.
const CLIENT_GEOCODE_TIMEOUT_MS = 30000;
const CLIENT_BULK_GEOCODE_TIMEOUT_MS = 20 * 60 * 1000;

export const clientsAPI = {
  getClients: (params) => apiClient.get('/clients', { params: sanitizeParams(params) }),
  getClient: (id) => apiClient.get(`/clients/${id}`),
  createClient: (data) => apiClient.post('/clients', data),
  techSubmitClient: (data) => apiClient.post('/clients/tech-submit', data),
  getPendingClients: () => apiClient.get('/clients/pending'),
  approveClient: (id) => apiClient.patch(`/clients/${id}/approve`),
  rejectClient: (id) => apiClient.delete(`/clients/${id}/reject`),
  updateClient: (id, data) => apiClient.put(`/clients/${id}`, data),
  updateClientGeneratorInfo: (id, data) => apiClient.put(`/clients/${id}/generator-info`, data),
  /** Technician-editable persistent note shown on every assignment card for this client. */
  updateTechNotes: (id, notes) => apiClient.put(`/clients/${id}/tech-notes`, { notes }),
  deactivateClient: (id) => apiClient.delete(`/clients/${id}`),
  reactivateClient: (id) => apiClient.patch(`/clients/${id}/reactivate`),
  getContractClientsNeedingService: (params) => apiClient.get('/clients/contract/needing-service', { params: sanitizeParams(params) }),
  getServiceHistoryOverview: (params) => apiClient.get('/clients/service-history-overview', { params: sanitizeParams(params) }),
  assignContractClient: (data) => apiClient.post('/clients/contract/assign', data),
  getWeeklyAssignments: () => apiClient.get('/clients/contract/weekly-assignments'),
  queueContractClient: (data) => apiClient.post('/clients/contract/queue', data),
  getQueuedServiceCalls: () => apiClient.get('/clients/contract/queue'),
  getQueuedServiceCallCount: () => apiClient.get('/clients/contract/queue/count'),
  updateQueuedServiceCall: (id, data) => apiClient.patch(`/clients/contract/queue/${id}`, data),
  removeQueuedServiceCall: (id) => apiClient.delete(`/clients/contract/queue/${id}`),
  unassignContractClient: (serviceCallId) => apiClient.delete(`/clients/contract/unassign/${serviceCallId}`),
  markClientDone: (clientId, data = {}) => apiClient.post(`/clients/contract/${clientId}/mark-done`, data),
  skipClientToNextCycle: (clientId, data = {}) => apiClient.post(`/clients/contract/${clientId}/skip-to-next-cycle`, data),
  pushBackClient: (clientId, data = {}) => apiClient.post(`/clients/contract/${clientId}/push-back`, data),
  assignPriorityServiceCall: (data) => apiClient.post('/clients/priority/assign', data),
  getPriorityAssignments: () => apiClient.get('/clients/priority/assignments'),
  unassignPriorityServiceCall: (serviceCallId) => apiClient.delete(`/clients/priority/unassign/${serviceCallId}`),
  getDuplicateCities: () => apiClient.get('/clients/cities/duplicates'),
  standardizeCity: (data) => apiClient.post('/clients/cities/standardize', data),
  getAddressFormattingIssues: () => apiClient.get('/clients/addresses/formatting-issues'),
  standardizeAddress: (clientId, data) => apiClient.put(`/clients/addresses/${clientId}/standardize`, data),
  getZipCodeIssues: () => apiClient.get('/clients/addresses/zipcode-issues', { timeout: CLIENT_BULK_GEOCODE_TIMEOUT_MS }),
  autoResolveZipCodes: () => apiClient.post('/clients/addresses/auto-resolve-zipcodes', undefined, { timeout: CLIENT_BULK_GEOCODE_TIMEOUT_MS }),
  resolveZipCode: (clientId, data) => apiClient.put(`/clients/addresses/${clientId}/resolve-zipcode`, data),
  getUnresolvedZipCodes: () => apiClient.get('/clients/addresses/unresolved-zipcodes'),
  geocodeAllClients: () => apiClient.post('/clients/addresses/geocode-all', undefined, { timeout: CLIENT_BULK_GEOCODE_TIMEOUT_MS }),
  geocodeClient: (clientId) => apiClient.post(`/clients/${clientId}/geocode`, undefined, { timeout: CLIENT_GEOCODE_TIMEOUT_MS }),
  getGeocodingFailures: () => apiClient.get('/clients/addresses/geocoding-failures'),
  importClients: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('/clients/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 300000, // 5 minutes timeout for large imports
    });
  },
  clearGeneratorInfo: () => apiClient.post('/clients/clear-generator-info'),
  checkMonthTransition: () => apiClient.get('/clients/contract/month-transition-check'),
  bulkCarryOverClients: (clientIds) => apiClient.post('/clients/contract/bulk-carryover', { clientIds }),
  getMonthPairDiff: () => apiClient.get('/clients/contract/month-pair-diff'),
};


// Service Calls API
export const serviceCallsAPI = {
  getServiceCalls: (params, axiosConfig = {}) =>
    apiClient.get('/service-calls', { params: sanitizeParams(params), ...axiosConfig }),
  getServiceCall: (id) => apiClient.get(`/service-calls/${id}`),
  createServiceCall: (data) => apiClient.post('/service-calls', data),
  createTechnicianServiceCall: (data) => apiClient.post('/service-calls/technician', data),
  updateServiceCall: (id, data) => apiClient.put(`/service-calls/${id}`, data),
  deleteServiceCall: (id) => apiClient.delete(`/service-calls/${id}`),
  bulkDeleteServiceCalls: (serviceCallIds) => apiClient.post('/service-calls/bulk-delete', { serviceCallIds }),
  deleteAllServiceCalls: () => apiClient.delete('/service-calls'),
  getDailyClients: (date) => apiClient.get(`/service-calls/daily/${date}`),
  getDailySupplies: (date) => apiClient.get(`/service-calls/daily/${date}/supplies`),
  getDailyRoute: (date) => apiClient.get(`/service-calls/daily/${date}/route`),
  getWeeklyAssignments: () => apiClient.get('/service-calls/weekly-assignments'),
  getPriorityAssignments: () => apiClient.get('/service-calls/priority-assignments'),
  getCalendarServiceCalls: (params) => apiClient.get('/service-calls/calendar', { params: sanitizeParams(params) }),
  getTodayOverview: (params) => apiClient.get('/service-calls/today-overview', { params: sanitizeParams(params) }),
  getUnscheduledServiceCalls: (params) => apiClient.get('/service-calls/unscheduled', { params: sanitizeParams(params) }),
  getSchedulingBacklog: (params) => apiClient.get('/service-calls/scheduling-backlog', { params: sanitizeParams(params) }),
  getOverdueScheduled: (params) => apiClient.get('/service-calls/overdue-scheduled', { params: sanitizeParams(params) }),
  scheduleServiceCall: (id, data) => apiClient.patch(`/service-calls/${id}/schedule`, data),
  unscheduleServiceCall: (id) => apiClient.patch(`/service-calls/${id}/unschedule`),
  finalizeServiceCall: (id) => apiClient.patch(`/service-calls/${id}/finalize`),
  toggleCorrectionFlag: (id, data) => apiClient.patch(`/service-calls/${id}/correction-flag`, data),
  setScheduleLock: (id, data) => apiClient.patch(`/service-calls/${id}/schedule-lock`, data),
  setStar: (id, isStarred) => apiClient.patch(`/service-calls/${id}/star`, { isStarred }),
  updateChecklist: (id, data) => apiClient.patch(`/service-calls/${id}/checklist`, data),
  sendFranklinCompletion: (id, data) => apiClient.post(`/service-calls/${id}/send-franklin`, data),
  getMonthlyOilFilters: (month) => apiClient.get('/service-calls/monthly-oil-filters', { params: { month } }),
  getMonthlyFuelFilters: (month) => apiClient.get('/service-calls/monthly-fuel-filters', { params: { month } }),
};

// Service Call Visits API
export const serviceCallVisitsAPI = {
  getVisits: (serviceCallId) => apiClient.get(`/service-calls/${serviceCallId}/visits`),
  createVisit: (serviceCallId, data) => apiClient.post(`/service-calls/${serviceCallId}/visits`, data),
  getVisit: (serviceCallId, visitId) => apiClient.get(`/service-calls/${serviceCallId}/visits/${visitId}`),
  updateVisit: (serviceCallId, visitId, data) => apiClient.put(`/service-calls/${serviceCallId}/visits/${visitId}`, data),
  deleteVisit: (serviceCallId, visitId) => apiClient.delete(`/service-calls/${serviceCallId}/visits/${visitId}`),
};

// Service Call Types API
export const serviceCallTypesAPI = {
  getServiceCallTypes: (params) => apiClient.get('/service-call-types', { params: sanitizeParams(params) }),
  getServiceCallType: (id) => apiClient.get(`/service-call-types/${id}`),
  createServiceCallType: (data) => apiClient.post('/service-call-types', data),
  updateServiceCallType: (id, data) => apiClient.put(`/service-call-types/${id}`, data),
  deleteServiceCallType: (id) => apiClient.delete(`/service-call-types/${id}`),
};

// Client Flags API
export const clientFlagsAPI = {
  getClientFlags: (params) => apiClient.get('/client-flags', { params: sanitizeParams(params) }),
  getClientFlag: (id) => apiClient.get(`/client-flags/${id}`),
  createClientFlag: (data) => apiClient.post('/client-flags', data),
  updateClientFlag: (id, data) => apiClient.put(`/client-flags/${id}`, data),
  deleteClientFlag: (id) => apiClient.delete(`/client-flags/${id}`),
};

// Inventory API
export const inventoryAPI = {
  getInventory: (params) => apiClient.get('/inventory', { params: sanitizeParams(params) }),
  getInventoryItem: (id) => apiClient.get(`/inventory/${id}`),
  createInventoryItem: (data) => apiClient.post('/inventory', data),
  updateInventoryItem: (id, data) => apiClient.put(`/inventory/${id}`, data),
  deleteInventoryItem: (id) => apiClient.delete(`/inventory/${id}`),
  getStock: (id) => apiClient.get(`/inventory/${id}/stock`),
  getTransactions: (id, params) => apiClient.get(`/inventory/${id}/transactions`, { params: sanitizeParams(params) }),
  receiveStock: (id, data) => apiClient.post(`/inventory/${id}/receive`, data),
  adjustStock: (id, data) => apiClient.post(`/inventory/${id}/adjust`, data),
  transferStock: (data) => apiClient.post('/inventory/transfer', data),
  importInventory: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('/inventory/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 300000, // 5 minutes timeout for large imports
    });
  },
};

// Inventory Locations API
export const inventoryLocationsAPI = {
  getLocations: (params) => apiClient.get('/inventory-locations', { params: sanitizeParams(params) }),
  createLocation: (data) => apiClient.post('/inventory-locations', data),
  updateLocation: (id, data) => apiClient.put(`/inventory-locations/${id}`, data),
  ensureTruckLocation: (technicianId) => apiClient.post(`/inventory-locations/ensure-truck/${technicianId}`),
  getMyTruckStock: (params) => apiClient.get('/inventory-locations/my-truck-stock', { params: sanitizeParams(params) }),
};

// Parts Needed API
export const partsNeededAPI = {
  getPartsNeeded: (params) => apiClient.get('/parts-needed', { params: sanitizeParams(params) }),
  getPartNeeded: (id) => apiClient.get(`/parts-needed/${id}`),
  createPartNeeded: (data) => apiClient.post('/parts-needed', data),
  updatePartNeeded: (id, data) => apiClient.put(`/parts-needed/${id}`, data),
  deletePartNeeded: (id) => apiClient.delete(`/parts-needed/${id}`),
  getPartsNeededSummary: () => apiClient.get('/parts-needed/summary')
};

// Reports API
export const reportsAPI = {
  getTechMetrics: (params) => apiClient.get('/reports/tech-metrics', { params: sanitizeParams(params) }),
  getTechCorrections: (params) => apiClient.get('/reports/tech-corrections', { params: sanitizeParams(params) }),
};

// CSpire API
export const cspireAPI = {
  getSites: (params) => apiClient.get('/cspire/sites', { params: sanitizeParams(params) }),
  createSite: (data) => apiClient.post('/cspire/sites', data),
  updateSite: (id, data) => apiClient.put(`/cspire/sites/${id}`, data),
  /** Technician-only: generator / filter fields when assigned to this C Spire site (or needs-tech-info). */
  updateSiteByTechnician: (id, data) => apiClient.patch(`/cspire/sites/${id}/technician-update`, data),
  /** Technician-editable persistent note shown on every assignment card for this CSpire site. */
  updateSiteTechNotes: (id, notes) => apiClient.put(`/cspire/sites/${id}/tech-notes`, { notes }),
  getWorkOrders: (params) => apiClient.get('/cspire/work-orders', { params: sanitizeParams(params) }),
  createWorkOrder: (data) => apiClient.post('/cspire/work-orders', data),
  updateWorkOrder: (id, data) => apiClient.put(`/cspire/work-orders/${id}`, data),
  deleteWorkOrder: (id, { force = false } = {}) => apiClient.delete(`/cspire/work-orders/${id}`, { params: force ? { force: 'true' } : {} }),
  deleteAllWorkOrders: () => apiClient.delete('/cspire/work-orders'),
  findDuplicateWorkOrders: () => apiClient.get('/cspire/work-orders/duplicates'),
  updateWorkOrderSchedule: (id, data) => apiClient.patch(`/cspire/work-orders/${id}/schedule`, data),
  assignWorkOrder: (id, data) => apiClient.post(`/cspire/work-orders/${id}/assign`, data),
  getPriceSchedules: (params) => apiClient.get('/cspire/price-schedules', { params: sanitizeParams(params) }),
  createPriceSchedule: (data) => apiClient.post('/cspire/price-schedules', data),
  updatePriceSchedule: (id, data) => apiClient.put(`/cspire/price-schedules/${id}`, data),
  deletePriceSchedule: (id) => apiClient.delete(`/cspire/price-schedules/${id}`)
};

// Franklin API
export const franklinAPI = {
  getSites: (params) => apiClient.get('/franklin/sites', { params: sanitizeParams(params) }),
  getSite: (id) => apiClient.get(`/franklin/sites/${id}`),
  createSite: (data) => apiClient.post('/franklin/sites', data),
  updateSite: (id, data) => apiClient.put(`/franklin/sites/${id}`, data),
  /** Technician-only: equipment fields when assigned to this Franklin site (or needs-tech-info). */
  updateSiteByTechnician: (id, data) => apiClient.patch(`/franklin/sites/${id}/technician-update`, data),
  /** Technician-editable persistent note shown on every assignment card for this Franklin site. */
  updateSiteTechNotes: (id, notes) => apiClient.put(`/franklin/sites/${id}/tech-notes`, { notes }),
  assignSite: (id, data) => apiClient.post(`/franklin/sites/${id}/assign`, data),
  getSitesNeedingService: () => apiClient.get('/franklin/sites/needing-service'),
  importSites: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('/franklin/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000
    });
  }
};

// Map API
export const mapAPI = {
  getPoints: (params) => apiClient.get('/map/points', { params: sanitizeParams(params) }),
  getTechnicians: () => apiClient.get('/map/technicians'),
  getTechnicianLocationHistory: (id, params) =>
    apiClient.get(`/map/technicians/${id}/location-history`, { params: sanitizeParams(params) }),
  planRoute: (data) => apiClient.post('/map/route-plan', data),
  getRoute: (data) => apiClient.post('/map/route', data),
  geocodeCspireSites: () => apiClient.post('/map/geocode/cspire'),
  geocodeFranklinSites: () => apiClient.post('/map/geocode/franklin'),
  geocodeAddress: (data) => apiClient.post('/map/geocode', data),
};

// Devices API
export const devicesAPI = {
  recordLocation: (data) => apiClient.post('/devices/location', data),
  getWebPushPublicKey: () => apiClient.get('/devices/web-push/vapid-public-key'),
  registerWebPush: (data) => apiClient.post('/devices/web-push', data),
  unregisterWebPush: (data) => apiClient.delete('/devices/web-push', { data }),
};

// Auto-schedule API
// Preview/apply build a full travel-time matrix (self-hosted OSRM, which
// itself allows up to 15s) on top of the scheduling queries, so a busy
// date range can easily take longer than the default 10s client timeout.
// recompute-slot is scoped to a single tech/day so it stays lighter, but
// still gets a bit of headroom since it hits the same OSRM path.
const AUTO_SCHEDULE_TIMEOUT_MS = 60000;
export const autoScheduleAPI = {
  preview: (data) => apiClient.post('/auto-schedule/preview', data, { timeout: AUTO_SCHEDULE_TIMEOUT_MS }),
  previewCompare: (data) => apiClient.post('/auto-schedule/preview-compare', data, { timeout: AUTO_SCHEDULE_TIMEOUT_MS }),
  apply: (data) => apiClient.post('/auto-schedule/apply', data, { timeout: AUTO_SCHEDULE_TIMEOUT_MS }),
  recomputeSlot: (data) => apiClient.post('/auto-schedule/recompute-slot', data, { timeout: 30000 }),
  getIncompleteDays: () => apiClient.get('/auto-schedule/incomplete-days'),
  acknowledgeIncompleteDay: (data) => apiClient.post('/auto-schedule/incomplete-days/acknowledge', data),
  previewIncompleteDay: (data) => apiClient.post('/auto-schedule/preview-incomplete-day', data, { timeout: AUTO_SCHEDULE_TIMEOUT_MS }),
  getSqueezeCandidates: (params) => apiClient.get('/auto-schedule/squeeze-candidates', { params: sanitizeParams(params), timeout: AUTO_SCHEDULE_TIMEOUT_MS }),
  getRepairPlacements: (params) => apiClient.get('/auto-schedule/repair-placements', { params: sanitizeParams(params), timeout: AUTO_SCHEDULE_TIMEOUT_MS }),
};

// Scheduling settings API
export const schedulingSettingsAPI = {
  get: () => apiClient.get('/scheduling-settings'),
  update: (data) => apiClient.put('/scheduling-settings', data),
};

// Schedule blackouts API
export const scheduleBlackoutsAPI = {
  getAll: (params) => apiClient.get('/schedule-blackouts', { params: sanitizeParams(params) }),
  create: (data) => apiClient.post('/schedule-blackouts', data),
  delete: (id) => apiClient.delete(`/schedule-blackouts/${id}`),
};

// Client schedule groups API
export const clientScheduleGroupsAPI = {
  getAll: (params) => apiClient.get('/client-schedule-groups', { params: sanitizeParams(params) }),
  get: (id) => apiClient.get(`/client-schedule-groups/${id}`),
  create: (data) => apiClient.post('/client-schedule-groups', data),
  update: (id, data) => apiClient.put(`/client-schedule-groups/${id}`, data),
  delete: (id) => apiClient.delete(`/client-schedule-groups/${id}`),
  addClient: (groupId, clientId) => apiClient.post(`/client-schedule-groups/${groupId}/clients`, { clientId }),
  removeClient: (groupId, clientId) => apiClient.delete(`/client-schedule-groups/${groupId}/clients/${clientId}`),
};

// AI Hub Assistant API (admin app)
export const aiAssistantAPI = {
  getStatus: () => apiClient.get('/ai-assistant/status'),
  confirmAction: (id) => apiClient.post(`/ai-assistant/actions/${id}/confirm`),
  cancelAction: (id) => apiClient.post(`/ai-assistant/actions/${id}/cancel`),
};

// Tech Assistant API (technician app)
export const techAssistantAPI = {
  getStatus: () => apiClient.get('/tech-assistant/status'),
  confirmAction: (id) => apiClient.post(`/tech-assistant/actions/${id}/confirm`),
  cancelAction: (id) => apiClient.post(`/tech-assistant/actions/${id}/cancel`),
};

/**
 * Stream chat with an AI assistant endpoint via SSE (fetch + ReadableStream).
 * @param {string} chatPath API path (e.g. '/ai-assistant/chat')
 * @param {Array<{role: string, content: string}>} messages
 * @param {{ signal?: AbortSignal, onEvent?: (event: string, data: object) => void }} options
 */
const streamChatAt = async (chatPath, messages, { signal, onEvent } = {}) => {
  const token = localStorage.getItem('authToken');
  const response = await fetch(`${API_BASE_URL}${chatPath}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ messages }),
    signal,
  });

  if (!response.ok) {
    let message = 'Chat request failed';
    try {
      const err = await response.json();
      message = err.message || message;
    } catch {
      // ignore parse errors
    }
    throw new Error(message);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() || '';

    for (const part of parts) {
      if (!part.trim()) continue;
      let event = 'message';
      let dataStr = '';
      for (const line of part.split('\n')) {
        if (line.startsWith('event: ')) event = line.slice(7);
        if (line.startsWith('data: ')) dataStr = line.slice(6);
      }
      if (dataStr) {
        try {
          onEvent?.(event, JSON.parse(dataStr));
        } catch {
          // ignore malformed SSE
        }
      }
    }
  }
};

export const streamAssistantChat = (messages, options) =>
  streamChatAt('/ai-assistant/chat', messages, options);

export const streamTechAssistantChat = (messages, options) =>
  streamChatAt('/tech-assistant/chat', messages, options);

/**
 * Persistent SSE connection for live message updates.
 */
export const connectMessageStream = async ({ signal, onEvent } = {}) => {
  const token = localStorage.getItem('authToken');
  const response = await fetch(`${API_BASE_URL}/messages/stream`, {
    method: 'GET',
    headers: {
      Accept: 'text/event-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    signal,
  });

  if (!response.ok) {
    let message = 'Message stream failed';
    try {
      const err = await response.json();
      message = err.message || message;
    } catch {
      // ignore parse errors
    }
    throw new Error(message);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() || '';

    for (const part of parts) {
      if (!part.trim()) continue;
      let event = 'message';
      let dataStr = '';
      for (const line of part.split('\n')) {
        if (line.startsWith('event: ')) event = line.slice(7);
        if (line.startsWith('data: ')) dataStr = line.slice(6);
      }
      if (dataStr) {
        try {
          onEvent?.(event, JSON.parse(dataStr));
        } catch {
          // ignore malformed SSE
        }
      }
    }
  }
};

// Invoice API
export const invoicesAPI = {
  getByServiceCall: (serviceCallId) => apiClient.get(`/invoices/by-service-call/${serviceCallId}`),
  getInvoice: (id) => apiClient.get(`/invoices/${id}`),
  getNextNumber: () => apiClient.get('/invoices/next-number'),
  createInvoice: (serviceCallId) => apiClient.post('/invoices', { serviceCallId }),
  updateInvoice: (id, data) => apiClient.patch(`/invoices/${id}`, data),
  getPreviewHtml: (id) => apiClient.get(`/invoices/${id}/preview`, { responseType: 'text' }),
  downloadPdf: (id) => apiClient.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),
  getRecipients: (id) => apiClient.get(`/invoices/${id}/recipients`),
  sendInvoice: (id, toEmails) => apiClient.post(`/invoices/${id}/send`, { toEmails })
};

// Invoice settings API
export const invoiceSettingsAPI = {
  getSettings: () => apiClient.get('/invoice-settings'),
  updateSettings: (data) => apiClient.put('/invoice-settings', data)
};

export const salesTaxRatesAPI = {
  getRates: () => apiClient.get('/sales-tax-rates'),
  getClientStates: () => apiClient.get('/sales-tax-rates/states'),
  resolveTaxContext: (params) => apiClient.get('/sales-tax-rates/resolve', { params }),
  updateRates: (rates) => apiClient.put('/sales-tax-rates', { rates })
};

// Invoicing email-client folders API (mailboxes: customers, franklin, cspire)
export const invoiceFoldersAPI = {
  getFolders: (mailbox) => apiClient.get('/invoice-folders', { params: { mailbox } }),
  createFolder: (data) => apiClient.post('/invoice-folders', data),
  updateFolder: (id, data) => apiClient.patch(`/invoice-folders/${id}`, data),
  deleteFolder: (id) => apiClient.delete(`/invoice-folders/${id}`),
  getFolderServiceCalls: (id, params) => apiClient.get(`/invoice-folders/${id}/service-calls`, { params: sanitizeParams(params) }),
  assignToFolder: (id, serviceCallId) => apiClient.post(`/invoice-folders/${id}/assign`, { serviceCallId }),
  unassignFromFolder: (id, serviceCallId) => apiClient.delete(`/invoice-folders/${id}/assign/${serviceCallId}`)
};

// QuickBooks Online integration API (customer service calls only)
export const quickbooksAPI = {
  getStatus: () => apiClient.get('/quickbooks/status'),
  connect: () => apiClient.post('/quickbooks/connect'),
  disconnect: () => apiClient.post('/quickbooks/disconnect'),
  pushServiceCall: (id) => apiClient.post(`/quickbooks/push/${id}`),
  pushBulk: (serviceCallIds) => apiClient.post('/quickbooks/push-bulk', { serviceCallIds })
};

// Admin ↔ tech direct messaging API
export const messagesAPI = {
  getUnreadCount: () => apiClient.get('/messages/unread-count'),
  getThreads: () => apiClient.get('/messages/threads'),
  createThread: (data) => apiClient.post('/messages/threads', data),
  getThread: (id) => apiClient.get(`/messages/threads/${id}`),
  getMessages: (threadId, params) => apiClient.get(`/messages/threads/${threadId}/messages`, { params: sanitizeParams(params) }),
  sendMessage: (threadId, data) => apiClient.post(`/messages/threads/${threadId}/messages`, data),
  markThreadRead: (threadId) => apiClient.patch(`/messages/threads/${threadId}/read`),
  getLinkableServiceCalls: (params) => apiClient.get('/messages/linkable-service-calls', { params: sanitizeParams(params) }),
};

// Admin guided tutorial progress API
export const tutorialAPI = {
  getProgress: () => apiClient.get('/tutorial/progress'),
  updateProgress: (chapterId, data) => apiClient.put(`/tutorial/progress/${chapterId}`, data),
  resetChapter: (chapterId) => apiClient.post(`/tutorial/progress/${chapterId}/reset`),
  resetAll: () => apiClient.post('/tutorial/progress/reset-all'),
};

export default apiClient;
